/**
 * Destinos SCP de usuario (localStorage).
 * Independientes de PACS_CONFIG (default para GSPS/SR/estudio).
 */

export const SCP_DESTINATIONS_KEY = 'udv.scpDestinations'
export const SCP_SHARE_DEST_ID_KEY = 'udv.scpShareDestId'

export const SCP_CONNECTOR_STOW = 'stow-rs'
export const SCP_CONNECTOR_ORTHANC = 'orthanc-rest'
export const SCP_CONNECTOR_CSTORE = 'c-store-dimse'

/** Conectores implementados en el browser. */
export const SCP_CONNECTORS_ENABLED = [
  SCP_CONNECTOR_STOW,
  SCP_CONNECTOR_ORTHANC,
]

export const SCP_SHARE_DEST_DEFAULT = 'default'

function newId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID()
    }
  } catch (e) {
    /* ignore */
  }
  return `scp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function readRawList() {
  try {
    const raw = localStorage.getItem(SCP_DESTINATIONS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    return []
  }
}

function writeRawList(list) {
  try {
    localStorage.setItem(SCP_DESTINATIONS_KEY, JSON.stringify(list))
  } catch (e) {
    console.warn('[scpDestinations] save failed', e)
  }
}

/**
 * @param {object} raw
 * @returns {object|null}
 */
export function normalizeScpDestination(raw) {
  if (!raw || typeof raw !== 'object') return null
  const connector = String(raw.connector || SCP_CONNECTOR_STOW)
  if (
    connector !== SCP_CONNECTOR_STOW &&
    connector !== SCP_CONNECTOR_ORTHANC &&
    connector !== SCP_CONNECTOR_CSTORE
  ) {
    return null
  }
  const name = String(raw.name || '').trim()
  const IP = String(raw.IP || raw.host || '').trim()
  if (!name || !IP) return null
  const PUERTO = String(raw.PUERTO != null ? raw.PUERTO : raw.port || '443').trim()
  const PROTOCOLO = String(raw.PROTOCOLO || raw.protocol || 'https')
    .toLowerCase()
    .replace(/:$/, '')
  const BASE_PATH = String(raw.BASE_PATH != null ? raw.BASE_PATH : '').trim()
  return {
    id: raw.id ? String(raw.id) : newId(),
    name,
    connector,
    IP,
    PUERTO,
    PROTOCOLO: PROTOCOLO === 'http' ? 'http' : 'https',
    BASE_PATH,
    USUARIO: String(raw.USUARIO != null ? raw.USUARIO : ''),
    PASS: String(raw.PASS != null ? raw.PASS : ''),
    DICOMWEB_BASE: String(raw.DICOMWEB_BASE || '/dicom-web'),
    AETITLE: String(raw.AETITLE || ''),
  }
}

export function listScpDestinations() {
  return readRawList()
    .map(normalizeScpDestination)
    .filter(Boolean)
    .filter((d) => d.connector !== SCP_CONNECTOR_CSTORE)
}

export function getScpDestination(id) {
  if (!id || id === SCP_SHARE_DEST_DEFAULT) return null
  return listScpDestinations().find((d) => d.id === String(id)) || null
}

/**
 * @param {object} dest
 * @returns {{ ok: boolean, error?: string, dest?: object }}
 */
export function validateScpDestination(dest) {
  const n = normalizeScpDestination(dest)
  if (!n) return { ok: false, error: 'invalid' }
  if (n.connector === SCP_CONNECTOR_CSTORE) {
    return { ok: false, error: 'cstore_unavailable' }
  }
  if (!SCP_CONNECTORS_ENABLED.includes(n.connector)) {
    return { ok: false, error: 'connector' }
  }
  if (!n.name) return { ok: false, error: 'name' }
  if (!n.IP) return { ok: false, error: 'host' }
  const port = parseInt(n.PUERTO, 10)
  if (!Number.isFinite(port) || port < 1 || port > 65535) {
    return { ok: false, error: 'port' }
  }
  return { ok: true, dest: n }
}

/**
 * @param {object} dest
 * @returns {object|null} destino guardado
 */
export function upsertScpDestination(dest) {
  const v = validateScpDestination(dest)
  if (!v.ok || !v.dest) return null
  const next = v.dest
  const list = listScpDestinations()
  const idx = list.findIndex((d) => d.id === next.id)
  if (idx >= 0) list[idx] = next
  else list.push(next)
  writeRawList(list)
  return next
}

export function removeScpDestination(id) {
  if (!id) return false
  const sid = String(id)
  const list = listScpDestinations().filter((d) => d.id !== sid)
  writeRawList(list)
  if (getScpShareDestId() === sid) {
    setScpShareDestId(SCP_SHARE_DEST_DEFAULT)
  }
  return true
}

export function getScpShareDestId() {
  try {
    const v = localStorage.getItem(SCP_SHARE_DEST_ID_KEY)
    if (!v || v === SCP_SHARE_DEST_DEFAULT) return SCP_SHARE_DEST_DEFAULT
    if (getScpDestination(v)) return v
    return SCP_SHARE_DEST_DEFAULT
  } catch (e) {
    return SCP_SHARE_DEST_DEFAULT
  }
}

export function setScpShareDestId(id) {
  const next =
    !id || id === SCP_SHARE_DEST_DEFAULT
      ? SCP_SHARE_DEST_DEFAULT
      : getScpDestination(id)
        ? String(id)
        : SCP_SHARE_DEST_DEFAULT
  try {
    localStorage.setItem(SCP_SHARE_DEST_ID_KEY, next)
  } catch (e) {
    /* ignore */
  }
  return next
}

/**
 * Shape compatible con stowStoreDicomInstance / storeDicomInstanceToPacs.
 * @param {object} dest
 */
export function toPacsConfigShape(dest) {
  const n = normalizeScpDestination(dest)
  if (!n) return null
  return {
    IP: n.IP,
    PUERTO: n.PUERTO,
    PROTOCOLO: n.PROTOCOLO,
    BASE_PATH: n.BASE_PATH,
    USUARIO: n.USUARIO,
    PASS: n.PASS,
    AETITLE: n.AETITLE || n.name,
    DICOMWEB_BASE: n.DICOMWEB_BASE || '/dicom-web',
    _scpId: n.id,
    _scpConnector: n.connector,
    _scpName: n.name,
  }
}

export function connectorLabelKey(connector) {
  if (connector === SCP_CONNECTOR_ORTHANC) return 'scpDlg.connectorOrthanc'
  if (connector === SCP_CONNECTOR_CSTORE) return 'scpDlg.connectorCstore'
  return 'scpDlg.connectorStow'
}
